import { account, databases, client, DATABASE_ID, COLLECTIONS, Query, ID } from '../lib/appwrite';

// OPTIMIZATION: Add simple in-memory cache with 30-second TTL
const cache = {
  allSamples: { data: null, timestamp: null },
  boxSamples: new Map(), // boxId -> { data, timestamp }
  ttl: 30000 // 30 seconds
};

/**
 * Service for managing samples with Appwrite integration
 * Handles snake_case ↔ camelCase conversion between database and React
 */

export const sampleService = {
  // OPTIMIZATION: Add cache invalidation method
  invalidateCache() {
    cache.allSamples = { data: null, timestamp: null };
    cache.boxSamples.clear();
  },

  /**
   * Get all samples for the current user with location details
   */
  async getAll() {
    try {
      // OPTIMIZATION: Check cache first
      const now = Date.now();
      if (cache.allSamples.data && cache.allSamples.timestamp &&
          (now - cache.allSamples.timestamp) < cache.ttl) {
        console.log('Returning cached samples data');
        return cache.allSamples.data;
      }

      const user = await account.get();
      if (!user) throw new Error('Not authenticated');

      // Fetch all samples
      const samplesResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.SAMPLES,
        [
          Query.orderDesc('$createdAt'),
          Query.limit(500)
        ]
      );

      // Fetch related data for each sample (box → rack → dewar)
      const transformed = await Promise.all(
        samplesResponse.documents.map(async (sample) => {
          try {
            // Fetch box
            const box = await databases.getDocument(
              DATABASE_ID,
              COLLECTIONS.BOXES,
              sample.box_id
            );

            // Fetch rack
            const rack = await databases.getDocument(
              DATABASE_ID,
              COLLECTIONS.RACKS,
              box.rack_id
            );

            // Fetch dewar
            const dewar = await databases.getDocument(
              DATABASE_ID,
              COLLECTIONS.DEWARS,
              rack.dewar_id
            );

            return {
              id: sample.sample_id,
              name: sample.name,
              freezeDate: sample.freeze_date,
              frozenBy: sample.frozen_by,
              pi: sample.principal_investigator,
              passageNumber: sample.passage_number,
              notes: sample.notes,
              capColour: sample.cap_color,
              gridLocation: {
                dewarId: dewar.$id,
                rackId: rack.$id,
                rackNumber: rack.rack_number,
                boxId: box.$id,
                boxName: box.name,
                position: sample.position
              }
            };
          } catch (error) {
            // If related data not found, return partial data
            console.error('Error fetching sample location:', error);
            return {
              id: sample.sample_id,
              name: sample.name,
              freezeDate: sample.freeze_date,
              frozenBy: sample.frozen_by,
              pi: sample.principal_investigator,
              passageNumber: sample.passage_number,
              notes: sample.notes,
              capColour: sample.cap_color,
              gridLocation: {
                dewarId: null,
                rackId: null,
                rackNumber: null,
                boxId: sample.box_id,
                boxName: null,
                position: sample.position
              }
            };
          }
        })
      );

      // OPTIMIZATION: Update cache
      cache.allSamples = { data: transformed, timestamp: now };

      return transformed;
    } catch (error) {
      console.error('Error fetching samples:', error);
      throw error;
    }
  },

  /**
   * Get samples for a specific box with grid positions
   */
  async getByBox(boxId) {
    try {
      // OPTIMIZATION: Check cache first
      const now = Date.now();
      const cachedBox = cache.boxSamples.get(boxId);
      if (cachedBox?.data && cachedBox?.timestamp &&
          (now - cachedBox?.timestamp) < cache.ttl) {
        console.log(`Returning cached box samples for ${boxId}`);
        return cachedBox.data;
      }

      const user = await account.get();
      if (!user) throw new Error('Not authenticated');

      // Fetch samples for this box
      const samplesResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.SAMPLES,
        [
          Query.equal('box_id', boxId),
          Query.limit(100)
        ]
      );

      // Get box details for grid size
      const box = await databases.getDocument(
        DATABASE_ID,
        COLLECTIONS.BOXES,
        boxId
      );

      const gridSize = box.box_type === '9x9' ? 9 : 10;

      // Create grid structure
      const grid = [];
      for (let row = 0; row < gridSize; row++) {
        for (let col = 0; col < gridSize; col++) {
          const position = `${String.fromCharCode(65 + row)}${col + 1}`;
          const sample = samplesResponse.documents.find(s => s.position === position);

          grid.push({
            position,
            row,
            col,
            occupied: !!sample,
            capColor: sample?.cap_color || null,
            sampleId: sample?.sample_id || null,
            sampleName: sample?.name || null,
            freezeDate: sample?.freeze_date || null,
            frozenBy: sample?.frozen_by || null,
            pi: sample?.principal_investigator || null,
            passageNumber: sample?.passage_number || null,
            notes: sample?.notes || null
          });
        }
      }

      // OPTIMIZATION: Update cache
      cache.boxSamples.set(boxId, { data: grid, timestamp: now });

      return grid;
    } catch (error) {
      console.error('Error fetching box samples:', error);
      throw error;
    }
  },

  /**
   * Create a new sample
   */
  async create(sampleData) {
    const user = await account.get();
    if (!user) throw new Error('Not authenticated');

    // Create sample
    const data = await databases.createDocument(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      ID.unique(),
      {
        box_id: sampleData.boxId,
        user_id: user.$id,
        sample_id: sampleData.sampleId,
        name: sampleData.name,
        position: sampleData.position,
        row_index: sampleData.rowIndex,
        col_index: sampleData.colIndex,
        freeze_date: sampleData.freezeDate,
        frozen_by: sampleData.frozenBy,
        principal_investigator: sampleData.pi,
        passage_number: sampleData.passageNumber || '',
        notes: sampleData.notes || '',
        cap_color: sampleData.capColor,
        original_sample_id: sampleData.originalSampleId || null
      }
    );

    // OPTIMIZATION: Invalidate cache after modification
    this.invalidateCache();

    return {
      id: data.sample_id,
      name: data.name,
      position: data.position,
      freezeDate: data.freeze_date,
      frozenBy: data.frozen_by,
      pi: data.principal_investigator,
      passageNumber: data.passage_number,
      notes: data.notes,
      capColour: data.cap_color,
      originalSampleId: data.original_sample_id
    };
  },

  /**
   * Update an existing sample
   */
  async update(sampleId, sampleData) {
    const user = await account.get();
    if (!user) throw new Error('Not authenticated');

    // Find the sample document by sample_id
    const samplesResponse = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      [
        Query.equal('sample_id', sampleId),
        Query.limit(1)
      ]
    );

    if (samplesResponse.documents.length === 0) {
      throw new Error('Sample not found');
    }

    const sampleDoc = samplesResponse.documents[0];

    // Update the sample
    const data = await databases.updateDocument(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      sampleDoc.$id,
      {
        name: sampleData.name,
        freeze_date: sampleData.freezeDate,
        frozen_by: sampleData.frozenBy,
        principal_investigator: sampleData.pi,
        passage_number: sampleData.passageNumber || '',
        notes: sampleData.notes || '',
        cap_color: sampleData.capColor
      }
    );

    // OPTIMIZATION: Invalidate cache after modification
    this.invalidateCache();

    return {
      id: data.sample_id,
      name: data.name,
      freezeDate: data.freeze_date,
      frozenBy: data.frozen_by,
      pi: data.principal_investigator,
      passageNumber: data.passage_number,
      notes: data.notes,
      capColour: data.cap_color
    };
  },

  /**
   * Delete a sample
   */
  async delete(sampleId) {
    const user = await account.get();
    if (!user) throw new Error('Not authenticated');

    // Find the sample document by sample_id
    const samplesResponse = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      [
        Query.equal('sample_id', sampleId),
        Query.limit(1)
      ]
    );

    if (samplesResponse.documents.length === 0) {
      throw new Error('Sample not found');
    }

    const sampleDoc = samplesResponse.documents[0];

    // Delete the sample
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      sampleDoc.$id
    );

    // OPTIMIZATION: Invalidate cache after modification
    this.invalidateCache();
  },

  /**
   * Delete sample by box and position
   */
  async deleteByPosition(boxId, position) {
    const user = await account.get();
    if (!user) throw new Error('Not authenticated');

    // Find the sample by box_id and position
    const samplesResponse = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      [
        Query.equal('box_id', boxId),
        Query.equal('position', position),
        Query.limit(1)
      ]
    );

    if (samplesResponse.documents.length === 0) {
      throw new Error('Sample not found at this position');
    }

    const sampleDoc = samplesResponse.documents[0];

    // Delete the sample
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      sampleDoc.$id
    );

    // OPTIMIZATION: Invalidate cache after modification
    this.invalidateCache();
  },

  /**
   * Bulk import samples into a box
   * @param {string} boxId - Box ID to import samples into
   * @param {Array} samples - Array of sample objects
   * @returns {Object} { success: number, failed: number, errors: Array }
   */
  async bulkImport(boxId, samples) {
    const user = await account.get();
    if (!user) throw new Error('Not authenticated');

    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    // Get box details to determine grid size
    const box = await databases.getDocument(
      DATABASE_ID,
      COLLECTIONS.BOXES,
      boxId
    );

    const gridSize = box.box_type === '9x9' ? 9 : 10;

    // Check existing samples in the box
    const existingSamplesResponse = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.SAMPLES,
      [
        Query.equal('box_id', boxId),
        Query.limit(100)
      ]
    );

    const occupiedPositions = new Set(existingSamplesResponse.documents.map(s => s.position) || []);

    // Helper function to convert position to row/col indices
    const positionToIndices = (position) => {
      const row = position.charCodeAt(0) - 65; // A=0, B=1, etc.
      const col = parseInt(position.substring(1)) - 1; // 1=0, 2=1, etc.
      return { row, col };
    };

    // Process each sample
    for (const sample of samples) {
      try {
        // Check if position is already occupied
        if (occupiedPositions.has(sample.position)) {
          results.failed++;
          results.errors.push(`Position ${sample.position} is already occupied`);
          continue;
        }

        // Get row and column indices
        const { row, col } = positionToIndices(sample.position);

        // Validate indices are within grid
        if (row >= gridSize || col >= gridSize) {
          results.failed++;
          results.errors.push(`Position ${sample.position} is outside grid for ${box.box_type} box`);
          continue;
        }

        // Generate unique sample_id
        const sampleId = `${boxId.substring(0, 8)}-${sample.position}-${Date.now()}`;

        // Insert sample
        await databases.createDocument(
          DATABASE_ID,
          COLLECTIONS.SAMPLES,
          ID.unique(),
          {
            box_id: boxId,
            user_id: user.$id,
            sample_id: sampleId,
            name: sample.name,
            position: sample.position,
            row_index: row,
            col_index: col,
            freeze_date: sample.freezeDate,
            frozen_by: sample.frozenBy,
            principal_investigator: sample.pi,
            passage_number: sample.passageNumber || '',
            notes: sample.notes || '',
            cap_color: sample.capColor || 'blue'
          }
        );

        results.success++;
        occupiedPositions.add(sample.position); // Track newly added position
      } catch (error) {
        results.failed++;
        results.errors.push(`${sample.position}: ${error.message}`);
      }
    }

    // Invalidate cache after bulk import
    this.invalidateCache();

    return results;
  },

  /**
   * Subscribe to realtime changes for samples
   * @param {Function} callback - Function to call when data changes
   * @returns {Function} unsubscribe function
   */
  subscribe(callback) {
    const channels = [
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.SAMPLES}.documents`
    ];

    const unsubscribe = client.subscribe(channels, async (response) => {
      console.log('Realtime sample event received:', response.events);
      // OPTIMIZATION: Invalidate cache and reload data
      this.invalidateCache();
      const data = await this.getAll();
      callback(data);
    });

    // Return unsubscribe function
    return unsubscribe;
  }
};
